/**
 * Which baked paint scheme a Famous Ace's own aeroplane wears in VIEW
 * AIRPLANES → Famous Aces (bug report: "the red baron's plane is not red but
 * yellow here"). `PlaneViewerInset`/`aircraft-preview.ts` had been calling
 * `show(modelId, undefined)` unconditionally — the same "factory scheme, no
 * repaint" path a pilot who never painted his mount gets in PERSONAL
 * AIRCRAFT — so an ace's own colours never reached the turntable at all.
 * Richthofen's factory scheme is `BARON.PAL` index 18, `rgb(166,130,52)`, a
 * yellow-tan — exactly the "yellow" the report describes; his real scheme is
 * index 30/28, `rgb(130,16,16)`/`rgb(113,8,8)`, unambiguously red.
 *
 * The recolour pipeline runs at *bake* time for this case, not at load time:
 * `packages/baker/src/model-paint.ts` resolves every scheme `RB.EXE`'s own
 * table (`aceOverrides`/`aceNames`, docs/formats/shading-and-paint.md §9)
 * lists for a fighter, and `tools/bake/src/stages/models.ts`'s
 * `paintVariantId` writes each one out as a whole separate manifest entry —
 * `models/FOKDR1@Richthofen`, not `models/FOKDR1` plus a runtime repaint (the
 * `PersonalPaint` a pilot's own wings/fuselage choices go through in
 * `flight/aircraft-mesh.ts` is a different, additive mechanism — the player's
 * two-part repaint layered on top of whichever whole entry loaded). So
 * showing an ace's livery here is a *model id* choice, the same one
 * `assets/paint-schemes.ts`'s dev viewer already makes off the manifest; this
 * table hardcodes the label the bake actually writes, checked against the
 * real bake output by `ace-livery.test.ts` (mirroring
 * `flight-baked-models.test.ts`'s own read-only-against-`data/baked` pattern
 * for `bakedModelIdOf`).
 *
 * Not every ace has one. `RB.EXE`'s own table paints 23 of the 25 named aces
 * at all — Hawker and Fonck fly stock colours even in the original — and two
 * more, Guynemer and Dallas, are painted onto a *different* airframe than the
 * one `mission-roster.ts`'s port-side ace→aircraft mapping assigns them
 * (Guynemer's scheme exists for `SPAD_7`, not the `SPAD_13` this port gives
 * him; Dallas's for `CAMEL`, not his `SOPTRI`). Those four are left out of
 * this table on purpose and fall back to the factory scheme here, exactly as
 * the original would for an aeroplane it never repainted for that ace.
 */

/** `FamousAce.id` → the scheme label `paintVariantId` bakes it under, for the 21 aces whose assigned mount really carries their own colours. */
const ACE_SCHEME_LABEL: Readonly<Record<string, string>> = {
  richthofen: "Richthofen",
  udet: "Udet",
  voss: "Voss",
  lothar: "Lothar",
  goering: "Goering",
  boelcke: "Boelcke",
  immelmann: "Immelmann",
  loewenhardt: "Loewenhardt",
  berthold: "Berthold",
  schleich: "Schleich",
  degelow: "Degelow",
  mannock: "Mannock",
  bishop: "Bishop",
  mccudden: "Mccudden",
  ball: "Ball",
  rickenbacker: "Rickenbacker",
  luke: "Luke",
  nungesser: "Nungesser",
  collishaw: "Collishaw",
  maclaren: "Maclaren",
  barker: "Barker",
};

/**
 * The baked model id to show for `aceId` flying `baseModelId` — the ace's own
 * scheme when the bake carries one for that airframe, the factory scheme
 * (`baseModelId` unchanged) otherwise.
 */
export function aceLiveryModelId(baseModelId: string, aceId: string): string {
  const label = ACE_SCHEME_LABEL[aceId];
  return label ? `${baseModelId}@${label}` : baseModelId;
}
